import React, { useMemo } from 'react';
import {
  Title2,
  Body1,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Toolbar,
  ToolbarButton,
  Select,
  SearchBox,
  type DataGridProps,
} from '@fluentui/react-components';
import {
  CheckmarkRegular,
  DismissRegular,
  ArrowDownloadRegular,
  ShareRegular,
} from '@fluentui/react-icons';
import { useFiscalEndReviewPageStyles } from '../styles/FiscalEndReviewPage.styles';
import { TIER_OPTIONS } from '../constants/tiers';
import { usePartnerReview } from '../hooks/usePartnerReview';
import { PartnerReviewGrid } from './PartnerReviewGrid';
import { PartnerDrawer } from './PartnerDrawer';
import { PartnerReviewInfoBar } from './PartnerReviewInfoBar';
import { SummaryCardBar, SUMMARY_CARD_COLORS } from './SummaryCardBar';
import { PaginationBar } from './PaginationBar';
import type { PartnerReviewConfig } from '../models/partnerReview';

interface PartnerReviewPageProps {
  config: PartnerReviewConfig;
}

export const PartnerReviewPage: React.FC<PartnerReviewPageProps> = ({ config }) => {
  const styles = useFiscalEndReviewPageStyles();
  const review = usePartnerReview(config);

  const defaultSortState = useMemo<DataGridProps['defaultSortState']>(
    () => ({ sortColumn: 'partnerName', sortDirection: 'ascending' }),
    [],
  );

  const summaryCards = useMemo(() => [
    { label: config.candidateLabel, value: review.candidates, accentColor: SUMMARY_CARD_COLORS.brand,    filterValue: 'All' },
    { label: 'Pending review',      value: review.pending,    accentColor: SUMMARY_CARD_COLORS.pending,  filterValue: 'Pending' },
    { label: 'Approved',            value: review.approved,   accentColor: SUMMARY_CARD_COLORS.approved, filterValue: 'Approved' },
    { label: 'Denied',              value: review.denied,     accentColor: SUMMARY_CARD_COLORS.denied,   filterValue: 'Denied' },
  ], [config.candidateLabel, review.candidates, review.pending, review.approved, review.denied]);

  return (
    <div className={styles.pageRoot}>
      <div className={styles.header}>
        <Title2>{config.pageTitle}</Title2>
        <div className={styles.subtitleRow}>
          <Body1>{config.pageSubtitle}</Body1>
        </div>
      </div>

      <PartnerReviewInfoBar ttmPeriodLabel={review.ttmPeriodLabel} lastUpdatedAt={review.lastUpdatedAt} />

      {review.error && (
        <div className={styles.messageBarWrapper}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Error</MessageBarTitle>
              {review.error}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      {review.actionError && (
        <div className={styles.messageBarWrapper}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Action failed</MessageBarTitle>
              {review.actionError}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      <SummaryCardBar cards={summaryCards} isLoading={review.isLoading} activeFilter={review.filterStatus} onCardClick={review.setFilterStatus} />

      <Toolbar className={styles.toolbar}>
        <ToolbarButton icon={<CheckmarkRegular />} disabled={review.selectedCount === 0 || review.submittingMpnIds.size > 0} onClick={review.onBulkApprove}>Approve</ToolbarButton>
        <ToolbarButton icon={<DismissRegular />} disabled={review.selectedCount === 0 || review.submittingMpnIds.size > 0} onClick={review.onBulkDeny}>Deny</ToolbarButton>
        <ToolbarButton icon={<ArrowDownloadRegular />} onClick={review.onExportToCsv}>Export to CSV</ToolbarButton>
        <ToolbarButton icon={<ShareRegular />} disabled>Share with Ops</ToolbarButton>
      </Toolbar>

      <div className={styles.filtersRow}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Search</label>
          <SearchBox placeholder="Partner name, TPID, MPN ID" onChange={(_e, data) => review.setSearchQuery(data.value.trim())} />
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="filter-current-tier" className={styles.filterLabel}>Current tier</label>
          <Select id="filter-current-tier" value={review.filterCurrentTier} onChange={(_e, data) => review.setFilterCurrentTier(data.value)}>
            <option>All</option>
            {TIER_OPTIONS.map((t) => <option key={t}>{t}</option>)}
          </Select>
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="filter-next-tier" className={styles.filterLabel}>{config.nextTierFilterLabel}</label>
          <Select id="filter-next-tier" value={review.filterNextTier} onChange={(_e, data) => review.setFilterNextTier(data.value)}>
            <option>All</option>
            {TIER_OPTIONS.map((t) => <option key={t}>{t}</option>)}
          </Select>
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="filter-contract-location" className={styles.filterLabel}>Contract location</label>
          <Select id="filter-contract-location" value={review.filterLocation} onChange={(_e, data) => review.setFilterLocation(data.value)}>
            <option>All</option>
            {review.locationOptions.map((loc) => <option key={loc}>{loc}</option>)}
          </Select>
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="filter-status" className={styles.filterLabel}>Status</label>
          <Select id="filter-status" value={review.filterStatus} onChange={(_e, data) => review.setFilterStatus(data.value)}>
            <option>All</option>
            <option>Pending</option>
            <option>Approved</option>
            <option>Denied</option>
          </Select>
        </div>
      </div>

      <PaginationBar
        totalItems={review.totalItems}
        startIndex={review.startIndex}
        endIndex={review.endIndex}
        currentPage={review.currentPage}
        totalPages={review.totalPages}
        pageOptions={review.pageOptions}
        onGoToPage={review.goToPage}
        selectedCount={review.selectedCount}
      />

      <div className={styles.tableWrapper}>
        <PartnerReviewGrid
          isLoading={review.isLoading}
          paginatedPartners={review.paginatedPartners}
          tierChangeLabel={config.tierChangeLabel}
          submittingMpnIds={review.submittingMpnIds}
          onSelectionChange={review.onSelectionChange}
          onRowClickCapture={review.onRowClickCapture}
          onPartnerClick={review.onPartnerClick}
          onApprove={review.onApprove}
          onDeny={review.onDeny}
          onReset={review.onReset}
          gridSelectionKey={review.gridSelectionKey}
          defaultSortState={defaultSortState}
          onSortChange={(col, dir) => review.setSortState({ sortColumn: col, sortDirection: dir })}
        />
      </div>

      <PartnerDrawer
        selectedPartner={review.selectedPartner}
        onClose={review.onCloseDrawer}
        drawerTab={review.drawerTab}
        onDrawerTabSelect={review.onDrawerTabSelect}
        selectedPartnerIndex={review.selectedPartnerIndex}
        sortedPartnersLength={review.sortedPartners.length}
        onNavigate={review.navigatePartner}
        tierChanges={review.tierChanges}
        isLoadingHistory={review.isLoadingHistory}
        notesEditorRef={review.notesEditorRef}
        initialNoteBody={review.initialNoteBody}
        initialNoteSystemPrefix={review.initialNoteSystemPrefix}
        isLoadingNotes={review.isLoadingNotes}
        allNotes={review.allNotes}
        onNoteSaved={review.onNoteSaved}
        onNotesHistoryLink={review.onNotesHistoryLink}
      />
    </div>
  );
};
